/**
 * F16.E: auto-sync the firm's `Firm-Skills` SharePoint site once per
 * portal session. Mounted by the `/c/${slug}` parent route.
 *
 * Trigger conditions (all must be true):
 *   1. The portal's `tenantBranding.firmSkillsSiteId` is non-empty.
 *   2. The per-portal MSAL instance has signed-in account.
 *   3. We haven't already synced for this (slug, siteId, driveId) tuple
 *      in this browser session.
 *
 * Demo mode is a no-op — demoSkills is seeded by `demo.seedForUser` in
 * F16.D. The hook is also a no-op when there's no Firm-Skills site
 * configured (some portals will go live without one).
 *
 * Storage: a sessionStorage key tracks last-sync to avoid hammering Graph
 * on every page reload. localStorage would be too aggressive — a fresh
 * tab should re-sync, since the SKILL.md library may have changed.
 *
 * NOTE: this hook reuses `useSkillSync` rather than orchestrating Graph
 * directly. That keeps the listSkills + upsertFromExternal flow in one
 * place (the SettingsWindow "Sync now" button uses the same path).
 */

import { useEffect, useRef } from 'react';
import { useSkillSync } from '../../hooks/useSkillSync';
import { useOptionalTenantBranding } from './TenantBrandingContext';
import { useOptionalPortalMsal } from './MsalInstanceProvider';

const SESSION_KEY_PREFIX = 'repancy:portal-skill-sync:';

function sessionKey(slug: string, siteId: string, driveId: string): string {
  return `${SESSION_KEY_PREFIX}${slug}::${siteId}::${driveId}`;
}

export function usePortalSkillsAutoSync(): void {
  const portal = useOptionalTenantBranding();
  const msal = useOptionalPortalMsal();
  const { sync } = useSkillSync();
  const firedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!portal) {return;}
    const { branding, slug } = portal;
    if (
      !branding.firmSkillsSiteId ||
      !branding.firmSkillsDriveId ||
      branding.firmSkillsSiteId.length === 0 ||
      branding.firmSkillsDriveId.length === 0
    ) {
      return;
    }
    if (!msal || !msal.account) {return;}
    const key = sessionKey(
      slug,
      branding.firmSkillsSiteId,
      branding.firmSkillsDriveId,
    );
    if (firedRef.current === key) {return;}
    try {
      if (sessionStorage.getItem(key) !== null) {
        firedRef.current = key;
        return;
      }
    } catch {
      // sessionStorage may be unavailable (incognito, third-party-cookie
      // settings). Fall through to firing the sync anyway — the ref guard
      // still prevents duplicate calls within a single page lifetime.
    }
    firedRef.current = key;
    void sync()
      .then(() => {
        try {
          sessionStorage.setItem(key, new Date().toISOString());
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        // useSkillSync already records the error in its status; we don't
        // surface anything additional here. The TenantChrome can read
        // the sync status via a future "synced N ago" pill (F16.F.2).
      });
  }, [portal, msal, sync]);
}
